import Breadcrumbs from "@/components/Breadcrumb";
import { PageSuspense } from "@/components/PageSuspense";
import { SkeletonText } from "@/components/podkit/loading/Skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/podkit/tabs/Tabs";
import { VisibilityControlledByFeatureFlag } from "@/components/VisibilityControlledByFeatureFlag";
import { ShareProjectButton } from "@/components/projects/ShareProjectButton";
import { TriggerPrebuildButton } from "@/components/projects/TriggerPrebuildButton";
import { ProjectGroupAccess } from "@/components/projects/ProjectGroupAccess";
import { Flags } from "@/feature-flags";
import { useFlagGroupManagementEnabled } from "@/hooks/use-flag";
import { useProject } from "@/queries/project-queries";
import { useCallback, useMemo, type FC } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { ProjectPhase } from "gitpod-next-api/gitpod/v1/project_pb";
import { Banner } from "@/components/flexkit/Banner";
import { Notification } from "@/components/flexkit/badge/Notification";
import { useProjectPrebuilds } from "@/queries/prebuild-queries";
import { isInProgressPrebuildPhase } from "@/utils/prebuilds";
import { CreateEnvironmentButton } from "@/routes/environments/create/CreateEnvironmentButton";

export const ProjectDetailsLayout: FC = () => {
    const { projectId } = useParams();
    const { data: project, error: getProjectError } = useProject(projectId);
    const location = useLocation();
    const navigate = useNavigate();
    const { value: isGroupManagementEnabled, loading: isLoadingGroupFlag } = useFlagGroupManagementEnabled();

    const currentTab = useMemo(() => {
        const path = location.pathname;
        if (path.includes("/secrets")) return "secrets";
        if (path.includes("/sharing")) return "sharing";
        if (path.includes("/prebuilds")) return "prebuilds";
        return "settings";
    }, [location.pathname]);

    const handleTabChange = useCallback(
        (value: string) => {
            navigate(value, { replace: true });
        },
        [navigate],
    );

    const isPendingDeletion = project?.desiredPhase === ProjectPhase.DELETED;
    const prebuildsQuery = useProjectPrebuilds(projectId);
    let prebuildCount = 0;
    if (prebuildsQuery.isSuccess && prebuildsQuery.data) {
        prebuildCount = prebuildsQuery.data.prebuilds?.length ?? 0;
    }

    const inProgressCount = useMemo(() => {
        if (!prebuildsQuery.data?.prebuilds) return 0;
        return prebuildsQuery.data.prebuilds.filter((pb) => isInProgressPrebuildPhase(pb?.status?.phase)).length;
    }, [prebuildsQuery.data?.prebuilds]);

    const deletionBannerText = useMemo(() => {
        if (!isPendingDeletion) return "";

        const prebuildText = prebuildCount === 1 ? "prebuild is" : "prebuilds are";
        return `This project is pending deletion. ${prebuildCount} ${prebuildText} being cleaned up and the project will be permanently deleted once complete.`;
    }, [isPendingDeletion, prebuildCount]);

    return (
        <div className="flex flex-col gap-4 sm:gap-6" data-testid="project-details-layout">
            {isPendingDeletion && <Banner variant="warning" text={deletionBannerText} />}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Breadcrumbs
                    label={
                        <SkeletonText
                            size="lg"
                            ready={Boolean(project)}
                            failed={Boolean(getProjectError)}
                            failedFallback={projectId}
                            className="w-[150px]"
                        >
                            {project?.metadata?.name}
                        </SkeletonText>
                    }
                />
                <div className="flex items-center gap-3">
                    <VisibilityControlledByFeatureFlag featureFlag={Flags.GroupManagementEnabled}>
                        <ProjectGroupAccess projectId={projectId!} />
                        <ShareProjectButton projectId={projectId!} />
                    </VisibilityControlledByFeatureFlag>
                    <TriggerPrebuildButton
                        projectId={projectId!}
                        environmentClassIds={project?.prebuildConfiguration?.environmentClassIds ?? []}
                    />
                    <CreateEnvironmentButton project={project} variant="primary" size="sm">
                        Create Environment
                    </CreateEnvironmentButton>
                </div>
            </div>
            <Tabs value={currentTab} onValueChange={handleTabChange}>
                <TabsList className="w-fit">
                    <TabsTrigger value="settings">Settings</TabsTrigger>
                    <TabsTrigger value="secrets">Secrets</TabsTrigger>
                    <TabsTrigger value="prebuilds">
                        Prebuilds
                        {inProgressCount > 0 && (
                            <Notification content={inProgressCount.toString()} className="ml-1.5" />
                        )}
                    </TabsTrigger>
                    {!isLoadingGroupFlag && !isGroupManagementEnabled && (
                        <TabsTrigger value="sharing">Sharing</TabsTrigger>
                    )}
                </TabsList>
            </Tabs>
            <PageSuspense>
                <Outlet />
            </PageSuspense>
        </div>
    );
};
